/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: SecretField.tsx
 🗓️ Created: 2025-01-08
 🧩 Description:
    Component for displaying sensitive information (passwords, API keys, tokens)
    with a blur effect that clears on hover. Provides secure display of secrets
    while allowing users to view them when needed.

 🔧 Notes:
    - Shows blurred content by default
    - Reveals content on hover
    - Supports copy-to-clipboard functionality
    - Can be used with or without input fields
    - Integrates with permission system

 🔁 Last Updated: 2025-01-08 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState } from 'react';
import type { MouseEvent } from 'react';
import { Eye, EyeOff, Copy, Check, RefreshCw } from 'lucide-react';
import SimpleTooltip from './SimpleTooltip';

interface SecretFieldProps {
  /** HTML id attribute */
  id?: string;
  /** The secret value to display */
  value: string;
  /** Whether to show as input field or read-only text */
  type?: 'input' | 'text';
  /** Placeholder text when value is empty */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Callback when value changes (only for input type) */
  onChange?: (value: string) => void;
  /** Whether to show copy button */
  showCopy?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show toggle visibility button */
  showToggle?: boolean;
  /** Custom blur class (default: blur-sm) */
  blurClass?: string;
  /** Error message to display below field */
  error?: string;
  /** Callback when generate button is clicked */
  onGenerate?: () => void;
  /** Whether to show generate button */
  showGenerate?: boolean;
}

/**
 * SecretField component for displaying sensitive information with blur effect
 * 
 * Features:
 * - Blurred by default, clears on hover
 * - Optional toggle button to lock/unlock blur
 * - Copy to clipboard functionality
 * - Works as both input and display field
 * 
 * @param {SecretFieldProps} props - Component props
 * @returns {JSX.Element} Rendered secret field component
 */
export default function SecretField({
  id,
  value,
  type = 'text',
  placeholder = '••••••••',
  disabled = false,
  onChange,
  showCopy = true,
  className = '',
  showToggle = true,
  blurClass = 'blur-sm',
  error,
  onGenerate,
  showGenerate = false
}: SecretFieldProps) {
  const [isHovered, setIsHovered] = useState(false);
  // For display mode: isVisible=true reveals the value
  // For input mode: isVisible=true switches type to 'text' (show password)
  const [isVisible, setIsVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  // Blur only applies to the read-only display mode
  const shouldBlur = !isVisible && !isHovered && value;

  // Handle copy to clipboard
  const handleCopy = async (e?: MouseEvent<HTMLButtonElement>) => {
    // Prevent event propagation to avoid triggering other handlers
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Get the actual value (not the blurred display)
    const valueToCopy = value || '';
    
    if (!valueToCopy) {
      console.warn('No value to copy');
      return;
    }
    
    try {
      // Use the Clipboard API if available
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(valueToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = valueToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        
        try {
          const successful = document.execCommand('copy');
          if (successful) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } else {
            console.error('Copy command failed');
          }
        } catch (err) {
          console.error('Fallback copy failed:', err);
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('Failed to copy:', error);
      // Try fallback method
      try {
        const textArea = document.createElement('textarea');
        textArea.value = valueToCopy;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        if (successful) {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
      }
    }
  };

  // Base input/text classes with explicit rounding
  const baseClasses = `
    w-full px-3 py-2 text-sm
    border ${error ? 'border-red-500 dark:border-red-400' : 'border-input'}
    bg-background text-foreground placeholder-muted-foreground 
    rounded-md
    focus:outline-none focus:ring-1 ${error ? 'focus:ring-red-500' : 'focus:ring-primary'} focus:border-transparent
    transition-all duration-200
    font-mono
    ${shouldBlur ? blurClass + ' select-none' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `.trim();

  return (
    <div className="w-full">
      <div className="relative flex items-center gap-1 w-full">
        <div
          className="relative flex-1 min-w-0"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
        {type === 'input' ? (
          <input
            id={id}
            type={isVisible ? 'text' : 'password'}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder || '••••••••'}
            disabled={disabled}
            autoComplete="new-password"
            className={`w-full px-3 py-2 text-sm border ${error ? 'border-red-500 dark:border-red-400' : 'border-input'} bg-background text-foreground placeholder-muted-foreground rounded-md focus:outline-none focus:ring-1 ${error ? 'focus:ring-red-500' : 'focus:ring-primary'} focus:border-transparent transition-all duration-200 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          />
        ) : (
            <div 
              className={`${baseClasses} min-h-[38px] flex items-center`} 
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              title={value}
            >
              {value || placeholder}
            </div>
          )}
        </div>

        {/* Action buttons container - always visible, no wrapping */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {/* Generate button */}
          {showGenerate && onGenerate && type === 'input' && (
            <SimpleTooltip content="Generate random password">
              <button
                type="button"
                onClick={onGenerate}
                className="p-1 rounded transition-colors text-muted-foreground hover:text-foreground hover:bg-accent flex-shrink-0"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
            </SimpleTooltip>
          )}
          
          {/* Toggle visibility button */}
          {showToggle && (
            <SimpleTooltip content={isVisible ? 'Hide' : 'Show'}>
              <button
                type="button"
                onClick={() => setIsVisible(!isVisible)}
                className={`
                  p-1 rounded transition-colors flex-shrink-0
                  ${isVisible
                    ? 'text-primary bg-primary/10 hover:bg-primary/20'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }
                `}
              >
                {isVisible ? (
                  <EyeOff className="h-3.5 w-3.5" />
                ) : (
                  <Eye className="h-3.5 w-3.5" />
                )}
              </button>
            </SimpleTooltip>
          )}

          {/* Copy button */}
          {showCopy && value && (
            <SimpleTooltip content={copied ? 'Copied!' : 'Copy'}>
              <button
                type="button"
                onClick={(e) => handleCopy(e)}
                onMouseDown={(e) => e.preventDefault()} // Prevent input blur
                className={`
                  p-1 rounded transition-colors flex-shrink-0
                  ${copied 
                    ? 'text-green-600 bg-green-50 dark:bg-green-900/20' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  }
                `}
                aria-label={copied ? 'Copied!' : 'Copy to clipboard'}
              >
                {copied ? (
                  <Check className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </SimpleTooltip>
          )}
      </div>
      </div>
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * SecretInput - A convenience wrapper for editable secret fields
 */
export function SecretInput({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  showCopy = false, // Default to false for input mode
  showToggle = true,
  ...props
}: Omit<SecretFieldProps, 'type'>) {
  return (
    <SecretField
      type="input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      showCopy={showCopy}
      showToggle={showToggle}
      {...props}
    />
  );
}

/**
 * SecretDisplay - A convenience wrapper for read-only secret fields
 */
export function SecretDisplay({
  value,
  placeholder = '••••••••',
  className,
  showCopy = true, // Default to true for display mode
  showToggle = true,
  blurClass,
}: Omit<SecretFieldProps, 'type' | 'onChange' | 'disabled'>) {
  return (
    <SecretField
      type="text"
      value={value}
      placeholder={placeholder}
      className={className}
      showCopy={showCopy}
      showToggle={showToggle}
      blurClass={blurClass}
    />
  );
}

